import { accessOf, billingText, planOf, roDate, TRIAL_AI_READS, type Company, t } from '@procumi/shared';
import { useState } from 'react';
import { useAsync } from '../hooks/useAsync';
import { api, errMsg } from '../lib/api';

/** „din 10” sau „nelimitat” */
const of = (used: number, max: number | null) =>
  max == null ? t('{0}, fără limită', used) : t('{0} din {1}', used, max);

/** „Contul firmei → Pachet”: ce ai, cât folosești, ce ai cerut. */
export function PlanSection({
  company,
  onChoose,
  toast,
}: {
  company: Company;
  /** lipsă: nu ești administrator */
  onChoose?: () => void;
  /** pentru eroarea portalului */
  toast?: (m: string) => void;
}) {
  const [opening, setOpening] = useState(false);
  const sub = company.subscription;
  const openPortal = async () => {
    setOpening(true);
    try {
      window.location.assign((await api.billingPortal()).url);
    } catch (e) {
      toast?.(errMsg(e));
      setOpening(false);
    }
  };
  const { data: usage } = useAsync(() => api.planUsage(), [], 'plan-usage');
  const access = accessOf(company);
  const plan = access.plan;
  const req = company.planRequest;

  const title =
    access.state === 'active'
      ? t('Pachetul {0}, {1}', plan!.name, billingText(company.billing) || 'lunar')
      : access.state === 'trial'
        ? t(
            'Probă gratuită, încă {0}',
            access.trialDaysLeft === 1 ? t('o zi') : t('{0} zile', access.trialDaysLeft),
          )
        : access.state === 'readonly'
          ? t('Fără pachet')
          : t('Cont fără limite');
  const lead =
    access.state === 'trial'
      ? t(
          'În probă ai limitele pachetului {0}. Proba se termină pe {1}.',
          plan!.name,
          roDate(new Date(Date.parse(company.trialEndsAt!) - 1).toISOString().slice(0, 10)),
        )
      : access.state === 'readonly'
        ? t('Vezi tot, dar nu poți crea sau schimba nimic până alegi un pachet.')
        : access.state === 'legacy'
          ? t('Firma ta e de dinainte de pachete și nu are limite.')
          : t(
              'Comparația, comenzile și istoricul sunt incluse în orice pachet. Diferă colegii, șantierele deschise și aprobarea comenzilor.',
            );

  return (
    <section className="sheet pad plan-section">
      <h2>{title}</h2>
      <p className="hint">{lead}</p>

      {usage ? (
        <dl className="plan-usage">
          <div>
            <dt>{t('Utilizatori')}</dt>
            <dd>{of(usage.users, plan ? plan.users : null)}</dd>
          </div>
          <div>
            <dt>{t('Șantiere active')}</dt>
            <dd>{of(usage.sites, plan ? plan.sites : null)}</dd>
          </div>
          {access.state === 'trial' ? (
            <div>
              <dt>{t('Documente citite automat')}</dt>
              <dd>{of(usage.aiReads, TRIAL_AI_READS)}</dd>
            </div>
          ) : null}
          {access.state === 'active' && plan?.aiMonthly != null ? (
            <div>
              <dt>{t('Documente citite automat luna asta')}</dt>
              <dd>{of(usage.aiMonthReads, plan.aiMonthly)}</dd>
            </div>
          ) : null}
        </dl>
      ) : null}
      {usage && plan?.users != null ? (
        <p className="hint" style={{ margin: 0 }}>
          {t(
            'La utilizatori se numără și invitațiile încă nefolosite. Oamenii de pe șantier (rolul „Șantier”) nu se numără.',
          )}
        </p>
      ) : null}

      {sub && sub.status !== 'canceled' ? (
        <p className={sub.status === 'past_due' ? 'read-warn' : 'hint'} style={{ margin: 0 }}>
          {sub.status === 'past_due'
            ? t(
                'Ultima plată cu cardul n-a trecut. Stripe mai încearcă; schimbă cardul ca pachetul să nu se oprească.',
              )
            : sub.cancelAtPeriodEnd
              ? t(
                  'Abonamentul e anulat: pachetul merge până pe {0}, apoi contul rămâne doar pentru citire.',
                  sub.periodEnd ? roDate(sub.periodEnd) : t('sfârșitul perioadei'),
                )
              : t(
                  'Plătești cu cardul. {0}: {1}.',
                  sub.status === 'trialing' ? t('Prima plată') : t('Următoarea plată'),
                  sub.periodEnd ? roDate(sub.periodEnd) : '—',
                )}
        </p>
      ) : null}

      {req ? (
        <p className="read-warn" style={{ margin: 0 }}>
          {t('Ai cerut pachetul')} {planOf(req.plan)?.name}
          {t(
            ', {0}, pe {1}. {2}',
            billingText(req.billing),
            roDate(req.at.slice(0, 10)),
            company.cardPayments
              ? t('Îți trimitem factura pe email; pachetul se activează când primim plata.')
              : t('Te contactăm în curând pentru pasul următor.'),
          )}
        </p>
      ) : null}

      {onChoose ? (
        <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
          <button
            className={`btn${access.state === 'active' ? '' : ' primary'}`}
            type="button"
            onClick={onChoose}
          >
            {req
              ? t('Schimbă cererea')
              : access.state === 'active'
                ? t('Schimbă pachetul')
                : t('Alege pachetul')}
          </button>
          {sub ? (
            <button className="btn" type="button" disabled={opening} onClick={() => void openPortal()}>
              {opening ? t('Se deschide…') : t('Facturi, card, anulare')}
            </button>
          ) : null}
        </div>
      ) : (
        <p className="hint" style={{ margin: 0 }}>
          {t('Pachetul îl alege un administrator al contului.')}
        </p>
      )}
    </section>
  );
}
